import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Truck, KeyRound, Delete, LogIn, UserRound, RotateCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const STORAGE_KEY = 'driver_pin_email';

const DriverPinLogin = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState('email'); // email | pin
  const [email, setEmail] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [pin, setPin] = useState('');
  const [driverName, setDriverName] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (targetEmail) => {
    const clean = (targetEmail || '').trim().toLowerCase();
    if (!clean) return;
    try {
      const res = await axios.get(`${API}/auth/driver-name-lookup`, { params: { email: clean } });
      if (res.data.name && res.data.has_pin) {
        setDriverName(res.data.name);
        setStep('pin');
        localStorage.setItem(STORAGE_KEY, clean);
      } else if (res.data.name && !res.data.has_pin) {
        toast.error('This driver has no PIN yet. Ask your admin to set one.');
      } else {
        toast.error('Driver not found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    }
  }, []);

  // Auto-advance to PIN pad if a saved email exists
  useEffect(() => {
    if (email && step === 'email') {
      lookup(email);
    }
     
  }, []);

  const submitPin = useCallback(async (value) => {
    const p = String(value);
    if (p.length < 4) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/driver-pin-login`, { email, pin: p });
      const { token, user } = res.data;
      setSession(user, token);
      toast.success(`Welcome, ${user.name}`);
      navigate('/driver');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wrong PIN');
      setPin('');
    } finally {
      setLoading(false);
    }
  }, [email, navigate, setSession]);

  const tap = (digit) => {
    if (pin.length >= 6) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === 4) {
      // Auto-submit at 4 digits — user can still type 5-6 by holding
      submitPin(next);
    }
  };

  const backspace = () => setPin((p) => p.slice(0, -1));
  const clearPin = () => setPin('');

  const changeDriver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setEmail('');
    setPin('');
    setDriverName('');
    setStep('email');
  };

  const initials = (driverName || '?')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center px-4" data-testid="driver-pin-page">
      <Card className="w-full max-w-md card-clay">
        <CardContent className="p-6 space-y-5">
          <div className="text-center space-y-1">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
              <Truck className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading text-xl font-bold">Driver Sign In</h1>
            <p className="text-xs text-muted-foreground">Enter your PIN to start your route</p>
          </div>

          {step === 'email' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                lookup(email);
              }}
              className="space-y-3"
              data-testid="driver-pin-email-form"
            >
              <label className="text-sm font-medium flex items-center gap-1.5">
                <UserRound className="w-4 h-4" /> Your email
              </label>
              <Input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="driver@yourcenter.com"
                data-testid="driver-pin-email-input"
              />
              <Button type="submit" className="w-full btn-primary" data-testid="driver-pin-continue">
                Continue
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                We&apos;ll remember your email on this tablet, so next time it&apos;s just tap-your-PIN.
              </p>
            </form>
          )}

          {step === 'pin' && (
            <div className="space-y-4" data-testid="driver-pin-pad-form">
              <div className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                  {initials}
                </div>
                <div className="font-semibold mt-2" data-testid="driver-pin-name">{driverName}</div>
                <button
                  type="button"
                  onClick={changeDriver}
                  className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 mt-0.5"
                  data-testid="driver-pin-change-driver"
                >
                  <RotateCcw className="w-3 h-3" /> Not you? Change driver
                </button>
              </div>

              {/* PIN dots */}
              <div className="flex justify-center gap-2" data-testid="driver-pin-dots">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 ${
                      i < pin.length ? 'bg-primary border-primary' : 'border-slate-300'
                    }`}
                  />
                ))}
                {pin.length > 4 && (
                  <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary" />
                )}
                {pin.length > 5 && (
                  <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary" />
                )}
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-2" data-testid="driver-pin-keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    onClick={() => tap(String(n))}
                    disabled={loading}
                    className="h-14 rounded-lg bg-white border border-border text-xl font-semibold hover:bg-muted active:scale-95 transition-transform"
                    data-testid={`driver-pin-key-${n}`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={clearPin}
                  disabled={loading || pin.length === 0}
                  className="h-14 rounded-lg bg-white border border-border text-sm font-medium hover:bg-muted disabled:opacity-40"
                  data-testid="driver-pin-clear"
                >
                  Clear
                </button>
                <button
                  onClick={() => tap('0')}
                  disabled={loading}
                  className="h-14 rounded-lg bg-white border border-border text-xl font-semibold hover:bg-muted active:scale-95 transition-transform"
                  data-testid="driver-pin-key-0"
                >
                  0
                </button>
                <button
                  onClick={backspace}
                  disabled={loading || pin.length === 0}
                  className="h-14 rounded-lg bg-white border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40"
                  data-testid="driver-pin-backspace"
                  aria-label="Backspace"
                >
                  <Delete className="w-5 h-5" />
                </button>
              </div>

              {pin.length >= 4 && (
                <Button
                  onClick={() => submitPin(pin)}
                  disabled={loading}
                  className="w-full btn-primary"
                  data-testid="driver-pin-submit"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              )}
            </div>
          )}

          <div className="text-center pt-2 border-t border-border/50">
            <button
              onClick={() => navigate('/login')}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="driver-pin-password-link"
            >
              <KeyRound className="w-3 h-3" /> Sign in with password instead
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverPinLogin;
